import { READY_TRIPS } from "./ready-trips-data";
import type { Trip } from "../types";

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/** Names the planner gives a trip by itself, as i18n keys. */
export const DEFAULT_TRIP_NAME_KEYS: readonly string[] = [
  "trip.default_name",
  "trip.ai_default_name",
  ...READY_TRIPS.map((r) => r.nameKey),
];

/**
 * The trip's name as this guest should read it (traveller tests, 2026-11-04).
 *
 * A name the planner chose ("My trip", "3 days in Marrakech") was saved in the
 * language the trip was made in, so /fr showed "My trip". A trip made since
 * carries `nameKey` and always reads in the page's language; an older one is
 * recognised when its name is that default in English or in the page's own
 * language (the only two the website has loaded). A name the guest typed is
 * shown as they typed it: renaming drops `nameKey`.
 */
export function tripDisplayName(
  trip: Pick<Trip, "name" | "nameKey">,
  t: Translate,
  /** English, for names saved before `nameKey` existed. */
  tEn?: Translate,
): string {
  if (trip.nameKey) return t(trip.nameKey);
  const name = trip.name.trim();
  for (const key of DEFAULT_TRIP_NAME_KEYS) {
    const here = t(key);
    if (here === key) continue;
    if (name === here) return here;
    if (tEn && name === tEn(key)) return here;
  }
  return trip.name;
}

/** The key behind a name the planner just chose, so it can follow the page's language. */
export function defaultNameKeyOf(name: string, t: Translate): string | undefined {
  const trimmed = name.trim();
  return DEFAULT_TRIP_NAME_KEYS.find((key) => t(key) === trimmed);
}
